import React, { useCallback, useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  $createParagraphNode
} from 'lexical';
import {
  $isHeadingNode,
  $createHeadingNode,
  HeadingTagType,
  $createQuoteNode
} from '@lexical/rich-text';
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  $isListNode,
  ListNode
} from '@lexical/list';
import { $setBlocksType } from '@lexical/selection';
import { $getNearestNodeOfType } from '@lexical/utils';
import CharacterCountPlugin from './CharacterCountPlugin';
import { formatNumber } from 'helpers/utils';
import Button from 'components/base/Button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBold,
  faItalic,
  faListUl,
  faListOl
} from '@fortawesome/free-solid-svg-icons';
import { faComment } from '@fortawesome/free-regular-svg-icons';

function ToolbarPlugin() {
  const [editor] = useLexicalComposerContext();
  const [blockType, setBlockType] = useState('paragraph');
  const [isBold, setIsBold] = useState(false);
  const [isItalic, setIsItalic] = useState(false);
  const [counts, setCounts] = useState({
    characters: 0,
    words: 0,
    tokens: 0
  });

  const updateToolbar = useCallback(() => {
    const selection = $getSelection();
    if ($isRangeSelection(selection)) {
      // Update text format
      setIsBold(selection.hasFormat('bold'));
      setIsItalic(selection.hasFormat('italic'));
      //setIsCode(selection.hasFormat('code'));

      // Update block type
      const anchorNode = selection.anchor.getNode();
      const element =
        anchorNode.getKey() === 'root'
          ? anchorNode
          : anchorNode.getTopLevelElementOrThrow();
      const elementKey = element.getKey();
      const elementDOM = editor.getElementByKey(elementKey);
      if (elementDOM !== null) {
        if ($isListNode(element)) {
          const parentList = $getNearestNodeOfType(anchorNode, ListNode);
          const type = parentList ? parentList.getTag() : element.getTag();
          setBlockType(type);
        } else {
          const type = $isHeadingNode(element)
            ? element.getTag()
            : element.getType();
          setBlockType(type);
        }
      }
    }
  }, [editor]);

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(() => {
        updateToolbar();
      });
    });
  }, [editor, updateToolbar]);

  const formatHeading = (headingSize: HeadingTagType) => {
    if (blockType !== headingSize) {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createHeadingNode(headingSize));
        }
      });
    }
  };

  const formatParagraph = () => {
    if (blockType !== 'paragraph') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createParagraphNode());
        }
      });
    }
  };

  const formatQuote = () => {
    if (blockType !== 'quote') {
      editor.update(() => {
        const selection = $getSelection();
        if ($isRangeSelection(selection)) {
          $setBlocksType(selection, () => $createQuoteNode());
        }
      });
    }
  };

  return (
    <>
      <CharacterCountPlugin onCountUpdate={setCounts} />
      <div className="toolbar bg-body border-bottom mb-0 pt-1 pb-1 mx-3 d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center justify-content-between">
          {/* Block Format */}
          <Button
            variant="link"
            onClick={() => formatHeading('h1')}
            title="Heading 1"
            className={`me-0 p-0 rounded-circle fs-10 lh-sm ${
              blockType === 'h1'
                ? 'text-white bg-primary-light'
                : 'text-secondary'
            }`}
            style={{ width: '24px', height: '24px' }}
          >
            H1
          </Button>
          <Button
            variant="link"
            onClick={() => formatHeading('h2')}
            title="Heading 2"
            className={`me-0 p-0 rounded-circle fs-10 ${
              blockType === 'h2'
                ? 'text-white bg-primary-light'
                : 'text-secondary'
            }`}
            style={{ width: '24px', height: '24px' }}
          >
            H2
          </Button>
          <Button
            variant="link"
            onClick={() => formatHeading('h3')}
            title="Heading 3"
            className={`me-0 p-0 rounded-circle fs-10 ${
              blockType === 'h3'
                ? 'text-white bg-primary-light'
                : 'text-secondary'
            }`}
            style={{ width: '24px', height: '24px' }}
          >
            H3
          </Button>
          <Button
            variant="link"
            onClick={formatParagraph}
            title="Normal Text"
            size="sm"
            className={`me-0 p-0 rounded-circle fs-10 ${
              blockType === 'paragraph'
                ? 'text-white bg-primary-light'
                : 'text-secondary'
            }`}
            style={{ width: '24px', height: '24px' }}
          >
            ab
          </Button>
          <Button
            variant="link"
            onClick={formatQuote}
            title="Quote / Comment"
            className={`me-0 p-0 rounded-circle fs-10 ${
              blockType === 'quote'
                ? 'text-white bg-primary-light'
                : 'text-secondary'
            }`}
            style={{ width: '24px', height: '24px' }}
          >
            <FontAwesomeIcon icon={faComment} />
          </Button>

          <span className="text-secondary-light mx-2">|</span>

          {/* Text Format */}
          <Button
            variant="link"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold');
            }}
            title="Bold (Ctrl+B)"
            className={`me-0 p-0 rounded-circle fs-10 ${
              isBold ? 'text-white bg-primary-light' : 'text-secondary'
            }`}
            style={{ width: '24px', height: '24px' }}
          >
            <FontAwesomeIcon icon={faBold} />
          </Button>
          <Button
            variant="link"
            onClick={() => {
              editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic');
            }}
            title="Italic (Ctrl+I)"
            className={`me-0 p-0 rounded-circle fs-10 ${
              isItalic ? 'text-white bg-primary-light' : 'text-secondary'
            }`}
            style={{ width: '24px', height: '24px' }}
          >
            <FontAwesomeIcon icon={faItalic} />
          </Button>
          {/* <Button
        variant="link"
        onClick={() => {
          editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'code');
        }}
        title="Inline Code"
        className={`me-0 p-0 rounded-circle fs-10 ${
          isCode ? 'text-white bg-primary-light' : 'text-secondary'
        }`}
        style={{ width: '24px', height: '24px' }}
      >
        <FontAwesomeIcon icon={faCode} />
      </Button> */}

          <span className="text-secondary-light mx-2">|</span>

          {/* Lists */}
          <Button
            variant="link"
            onClick={() => {
              editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
            }}
            title="Bullet List"
            className={`me-0 p-0 rounded-circle fs-10 ${
              blockType === 'ul'
                ? 'text-white bg-primary-light'
                : 'text-secondary'
            }`}
            style={{ width: '24px', height: '24px' }}
          >
            <FontAwesomeIcon icon={faListUl} />
          </Button>
          <Button
            variant="link"
            onClick={() => {
              editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
            }}
            title="Numbered List"
            className={`me-0 p-0 rounded-circle fs-10 ${
              blockType === 'ol'
                ? 'text-white bg-primary-light'
                : 'text-secondary'
            }`}
            style={{ width: '24px', height: '24px' }}
          >
            <FontAwesomeIcon icon={faListOl} />
          </Button>

          <span className="text-secondary-light mx-2">|</span>
        </div>
        {/* Counter Display */}
        <div className="text-secondary fs-10 pt-1 d-flex align-items-center lh-1">
          <span className="me-2">
            {formatNumber(counts.characters)} characters
          </span>
          -<span className="mx-2">{formatNumber(counts.words)} words</span>-
          <span className="mx-2 me-0">
            {formatNumber(counts.tokens)} tokens
          </span>
        </div>
      </div>
    </>
  );
}

export default ToolbarPlugin;
